import React, { useState } from "react";
import {
  Button,
  ButtonDropdown,
  Modal,
  FormField,
  Input,
  Box,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react";
import useSavedDashboardViews, {
  SavedDashboardView,
} from "../../hooks/useSavedDashboardViews";
import {
  BUILT_IN_VIEWS,
  BuiltInDashboardView,
  builtInView,
} from "./builtInViews";

export type DashboardViewSelection = SavedDashboardView | BuiltInDashboardView;

interface SavedViewsManagerProps {
  getFilters: (() => Promise<string>) | null;
  stage: string;
  onLoadView: (view: DashboardViewSelection | null) => void;
  activeViewName: string | null;
  setActiveViewName: (name: string | null) => void;
}

export const SavedViewsManager: React.FC<SavedViewsManagerProps> = ({
  getFilters,
  stage,
  onLoadView,
  activeViewName,
  setActiveViewName,
}) => {
  const { views, saveView, deleteView } = useSavedDashboardViews();
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [viewName, setViewName] = useState("");
  const [viewDescription, setViewDescription] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!viewName.trim()) {
      setError("Name is required");
      return;
    }
    if (!getFilters) return;

    setSaving(true);
    try {
      const filters = await getFilters();
      saveView(viewName.trim(), viewDescription.trim(), stage, filters);
      setSaveModalVisible(false);
      setViewName("");
      setViewDescription("");
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save view");
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    setSaveModalVisible(false);
    setViewName("");
    setViewDescription("");
    setError("");
  };

  const dropdownItems = [
    {
      id: "__default__",
      text: "Default filters",
      description: "Reset to published dashboard defaults",
    },
    {
      text: "Investigations",
      items: BUILT_IN_VIEWS.map((view) => ({
        id: view.id,
        text: view.name,
        description: view.description,
      })),
    },
    ...(views.length
      ? [
          {
            text: "Saved views",
            items: views.map((view) => ({
              id: view.id,
              text: view.name,
              description:
                view.description ||
                `${view.stage} — saved ${new Date(
                  view.createdAt
                ).toLocaleDateString()}`,
            })),
          },
        ]
      : []),
  ];

  return (
    <>
      <ButtonDropdown
        items={dropdownItems}
        onItemClick={({ detail }) => {
          if (detail.id === "__default__") {
            onLoadView(null);
            return;
          }
          const view =
            builtInView(detail.id) || views.find((v) => v.id === detail.id);
          if (view) {
            onLoadView(view);
          }
        }}
      >
        {activeViewName ? `View: ${activeViewName}` : "Views"}
      </ButtonDropdown>

      <Button
        iconName="star"
        variant="icon"
        onClick={() => setSaveModalVisible(true)}
        disabled={!getFilters}
        ariaLabel="Save current view"
      />

      <Modal
        visible={saveModalVisible}
        onDismiss={handleDismiss}
        header="Save current view"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDismiss}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Name" errorText={error}>
            <Input
              value={viewName}
              onChange={({ detail }) => {
                setViewName(detail.value);
                setError("");
              }}
              placeholder="e.g. US Prod - Chargebacks this month"
              autoFocus
            />
          </FormField>
          <FormField label="Description" description="Optional">
            <Textarea
              value={viewDescription}
              onChange={({ detail }) => setViewDescription(detail.value)}
              placeholder="What filters does this view represent?"
              rows={2}
            />
          </FormField>
          {views.length > 0 && (
            <FormField label="Existing saved views">
              <SpaceBetween size="xs">
                {views.map((view) => (
                  <div
                    key={view.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      background: "var(--color-background-container-content)",
                    }}
                  >
                    <span>
                      {view.name}{" "}
                      <span style={{ opacity: 0.6, fontSize: "12px" }}>
                        ({view.stage})
                      </span>
                    </span>
                    <Button
                      iconName="remove"
                      variant="icon"
                      onClick={() => {
                        if (activeViewName === view.name) {
                          onLoadView(null);
                        }
                        deleteView(view.id);
                      }}
                      ariaLabel={`Delete ${view.name}`}
                    />
                  </div>
                ))}
              </SpaceBetween>
            </FormField>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
};
